import { Link, useParams } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import { KpiStrip, KpiTile } from "@/shared/components/ui/kpi";
import { Card } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { useCampaignsList } from "../use-campaigns";
import { useLeadCounts } from "../use-leads";

const STAGES = ["Discover", "Review", "Enrich", "Freeze", "Sequence", "Launch", "Measure"] as const;

/**
 * Campaign Overview (screens.md §3) — real KPI strip + a pipeline strip whose
 * position is derived from the campaign's own signals, and the single next
 * action. All data is live; there is no analytics/events endpoint yet, so the
 * activity feed is intentionally omitted rather than faked.
 */
export function OverviewTab() {
  const { id } = useParams<{ id: string }>();
  const list = useCampaignsList();
  const leadsQ = useLeadCounts(id ?? "");

  const summary = list.data?.find((c) => c.id === id);
  const discovered = leadsQ.data?.total ?? 0;
  const approved = leadsQ.data?.counts?.APPROVED ?? 0;

  const contacts = summary?.contacts ?? 0;
  const sequences = summary?.sequences ?? 0;
  const sent = summary?.sent ?? 0;
  const opened = summary?.opened ?? 0;
  const status = summary?.status ?? "DRAFT";
  const openRate = sent > 0 ? `${Math.round((opened / sent) * 100)}%` : "--";

  // Pipeline position from real signals (best-effort; Overview is informational).
  let progress = 0;
  if (discovered > 0) progress = 1; // Discover done
  if (approved > 0) progress = 2; // Review done
  if (contacts > 0) progress = 3; // Enrich done
  if (sequences > 0) progress = 5; // Freeze + Sequence done
  if (status === "LAUNCHED") progress = 6; // Launch done
  if (sent > 0) progress = 7; // Measuring
  const currentStage = STAGES[Math.min(progress, STAGES.length - 1)];

  const next = nextAction(currentStage, id ?? "");
  // contacts/sent live in the summary; show a placeholder until it has loaded.
  const dash = (n: number) => (summary ? String(n) : "--");

  return (
    <div className="space-y-6">
      <KpiStrip>
        <KpiTile label="Discovered" value={leadsQ.data ? String(discovered) : "--"} />
        <KpiTile label="Approved" value={leadsQ.data ? String(approved) : "--"} />
        <KpiTile label="Contacts" value={dash(contacts)} />
        <KpiTile label="Sent" value={dash(sent)} />
        <KpiTile label="Open rate" value={openRate} highlighted />
      </KpiStrip>

      {/* Pipeline strip */}
      <Card className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        {STAGES.map((stage, i) => {
          const done = progress > i;
          const current = stage === currentStage && !done;
          return (
            <div key={stage} className="flex items-center gap-3">
              {current ? (
                <span className="inline-flex items-center gap-[6px] rounded-ge-param border border-ge-border-accent bg-ge-accent-tint px-[10px] py-1">
                  <span className="h-[6px] w-[6px] rounded-full bg-ge-accent" />
                  <span className="text-[12.5px] font-semibold text-ge-accent">{stage}</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-[6px]">
                  {done && <Check className="h-[14px] w-[14px] text-ge-accent" strokeWidth={2.4} />}
                  <span
                    className={cn(
                      "text-[12.5px] font-semibold",
                      done ? "text-ge-value" : "text-ge-secondary",
                    )}
                  >
                    {stage}
                  </span>
                </span>
              )}
              {i < STAGES.length - 1 && (
                <ChevronRight className="h-3 w-3 text-ge-ghost" strokeWidth={2.4} />
              )}
            </div>
          );
        })}
      </Card>

      {/* Next action — always exactly one, derived from the current stage. */}
      <Card className="p-5" data-testid="next-action">
        <p className="ge-eyebrow">NEXT ACTION</p>
        <p className="mt-2 text-[14.5px] text-ge-primary">{next.text}</p>
        <div className="mt-4">
          <Button asChild variant="accent-outline">
            <Link to={next.to}>{next.cta}</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}

function nextAction(stage: (typeof STAGES)[number], id: string) {
  switch (stage) {
    case "Discover":
      return { text: "Point the engine at a market to discover leads.", to: `/campaigns/${id}/discovery`, cta: "Run discovery" };
    case "Review":
      return { text: "Review the candidates and approve the ones worth reaching.", to: `/campaigns/${id}/leads`, cta: "Review leads" };
    case "Enrich":
      return { text: "Enrich the approved leads to find contact emails.", to: `/campaigns/${id}/enrichment`, cta: "Run enrichment" };
    case "Freeze":
      return { text: "Freeze the audience — the last barrier before enrolling.", to: `/campaigns/${id}/audience`, cta: "Freeze audience" };
    case "Sequence":
      return { text: "Author the copy and build the outreach sequence.", to: `/campaigns/${id}/sequences`, cta: "Build sequence" };
    case "Launch":
      return { text: "Everything's ready — launch the sequence.", to: `/campaigns/${id}/sequences`, cta: "Launch" };
    case "Measure":
    default:
      return { text: "The sequence is live. Watch opens and replies as they arrive.", to: `/campaigns/${id}/analytics`, cta: "View analytics" };
  }
}
